import { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { StepperHeader } from './StepperHeader';
import { ArrowLeftIcon, Info } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { PatientRequestProvider } from '../context/PatientRequestContext';
import { usePatientRequestContext } from '../context/usePatientRequestContext';
import { PATIENT_REQUEST_STEPS } from '../constants';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Separator,
  Alert,
  AlertTitle,
  AlertDescription,
} from '@predoc/cow';
import { RequestLoading } from './request-steps/request/RequestLoading';

const PageContent = () => {
  const { currentStepIndex, isLoading } = usePatientRequestContext();
  const CurrentStepComponent =
    PATIENT_REQUEST_STEPS[currentStepIndex].component;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <RequestLoading />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      {CurrentStepComponent && <CurrentStepComponent />}
    </div>
  );
};

export const EditPatientRequestPage = () => {
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();
  const [searchParams] = useSearchParams();
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const patientRequestId = searchParams.get('patientRequestId');

  // Get step from URL params (default to Request step)
  const stepParam = searchParams.get('step');
  const initialStep = stepParam ? parseInt(stepParam, 10) : 1;

  // Validate step index
  const validatedStep =
    initialStep >= 0 && initialStep < PATIENT_REQUEST_STEPS.length
      ? initialStep
      : 1;

  const handleGoBack = () => {
    navigate('/patients', { replace: true });
  };

  const handleDiscardDialogOpen = (isOpen: boolean) => {
    setIsDiscardDialogOpen(isOpen);
  };

  if (!patientId || !patientRequestId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-destructive mb-4 text-2xl font-bold">
            Invalid Request
          </h1>
          <Button onClick={() => navigate('/patients')}>
            Go Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PatientRequestProvider
      patientId={patientId}
      patientRequestId={patientRequestId}
      initialStep={validatedStep}
      syncUrl
    >
      <div className="min-h-screen bg-gray-50">
        <PageHeader>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => handleDiscardDialogOpen(true)}
              variant="ghost"
              size="icon"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold">Edit Patient Request</h1>
          </div>
          <StepperHeader />
        </PageHeader>
        <PageContent />
      </div>
      <Dialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
        <DialogContent className="p-0" aria-describedby={undefined}>
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Discard Changes</DialogTitle>
          </DialogHeader>
          <Separator />
          <div className="p-4">
            <Alert>
              <Info className="size-5" />
              <AlertTitle>Confirm Discard Changes</AlertTitle>
              <AlertDescription>
                All unsaved changes will be discarded.
              </AlertDescription>
            </Alert>
          </div>
          <Separator />
          <DialogFooter className="flex justify-end p-2 pt-0">
            <Button
              variant="outline"
              onClick={() => handleDiscardDialogOpen(false)}
            >
              Continue Editing
            </Button>
            <Button onClick={handleGoBack}>Discard Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PatientRequestProvider>
  );
};
